"use client";

import React from "react";
import { Check, Building2, Users, TrendingUp, ShieldCheck, Phone, Mail, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const sharedData = {
  phone: "(325) 555-1234",
  email: "info@tailletrealestate.com",
  address: "Abilene, Texas",
  benefits: ["Responsive communication", "Local market expertise", "Seamless tenant placement", "Maintenance coordination", "Financial reporting", "Maximized rental income"]
};
export default function ManagementInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="management-inquiry" className="relative w-full py-16 md:py-24 bg-background overflow-hidden flex items-center justify-center min-h-screen" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className={cn("flex flex-col gap-8 transition-all duration-700 delay-100", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10")}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight">
                Unlock Your Property's <span className="text-primary">True Potential</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Partner with Abilene's most dedicated property management team. 
                We handle the complexities of ownership so you can enjoy the rewards. 
                From tenant placement to financial reporting, we're here to serve you.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {sharedData.benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 p-1 rounded-full bg-primary/10 text-primary shrink-0">
                    <Check className="size-4" />
                  </div>
                  <span className="text-foreground font-medium">{benefit}</span>
                </div>)}
            </div>

            <div className="mt-4 p-6 bg-muted/30 border border-border rounded-lg">
              <h3 className="font-serif text-xl font-semibold mb-4">Direct Contact</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="size-5 text-primary" />
                  <span>(325) 529-3020</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="size-5 text-primary" />
                  <span>taillet.realestate@gmail.com</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Building2 className="size-5 text-primary" />
                  <span>{sharedData.address}</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={cn("w-full transition-all duration-700 delay-300", isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10")}>
            <FormProvider formId="management-inquiry-form" sectionName="Management Inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            property_type: "",
            units: "",
            services_interested: [],
            message: ""
          }}>
              <InquiryForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
          <Check className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Inquiry Sent!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Your Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="property_type" rules={{
        required: "Property type is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Property Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Mixed-use">Mixed-use</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="units" rules={{
        required: "Number of units is required",
        min: {
          value: 1,
          message: "Must be at least 1 unit"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Number of Units</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="services_interested" render={({
      field
    }) => <FormItem>
            <FormLabel className="mb-2 block">Management Services Interested In</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Tenant Placement", "Rent Collection", "Maintenance Coordination", "Financial Reporting", "Lease Renewals", "Other"].map((item, i) => <div key={item} data-index={i} className="flex items-center space-x-2">
                  <input type="checkbox" id={`service-${i}`} className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary" checked={field.value?.includes(item)} onChange={e => {
            const checked = e.target.checked;
            const current = field.value || [];
            if (checked) {
              field.onChange([...current, item]);
            } else {
              field.onChange(current.filter((val: string) => val !== item));
            }
          }} />
                  <label htmlFor={`service-${i}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                    {item}
                  </label>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Questions</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" placeholder="Provide any extra information about your property or specific needs..." {...field} />
            </FormControl>
            <FormDescription>Optional but helps us serve you better</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" className="w-full text-base py-6 font-semibold shadow-md" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending Request...
          </> : "Request Consultation"}
      </Button>
    </Form>;
}